<script setup>
import {
  ref,
  computed,
  useRoute,
  useSanityQuery,
  showError,
  useScrollTopOnMounted,
  useSanity,
  useAsyncData,
  useHead,
} from '#imports';
import { getQuery as getAuthorQuery } from '~/groq-queries/author';
import { getQuery as getAuthorArticlesQuery } from '~/groq-queries/author-articles';
import { getAuthorMetaTags } from '~/utils/get-author-meta-tags';

useScrollTopOnMounted();

const sanity = useSanity();
const route = useRoute();
const { slug } = route.params;
const articleChunkSize = 8;
const authorArticlesBlockSize = 5;
const offset = ref(authorArticlesBlockSize);
const limit = ref(articleChunkSize);
const moreArticles = ref([]);
const showMoreButton = ref(false);

const [{ data: author }, { data: totalCount }, { data: authorArticles }] =
  await Promise.all([
    useSanityQuery(getAuthorQuery({ slug })),
    useSanityQuery(getAuthorArticlesQuery({ slug, limit: 100 })),
    useSanityQuery(
      getAuthorArticlesQuery({
        slug,
        limit: authorArticlesBlockSize,
      })
    ),
  ]);

showMoreButton.value = totalCount.value.length > authorArticlesBlockSize;

const {
  pending,
  data: moreArticlesChunk,
  refresh,
} = await useAsyncData('moreAuthorArticles', () =>
  sanity.fetch(
    getAuthorArticlesQuery({
      slug,
      offset: offset.value,
      limit: limit.value,
    })
  )
);

const isDataEmpty = computed(() => !author.value?._id);
if (isDataEmpty.value)
  showError({ statusCode: 404, message: 'Страница не найдена' });

const pageTitle = computed(() => `${author.value.name} — DOXA`);

useHead({
  title: pageTitle,
  meta: isDataEmpty.value
    ? []
    : getAuthorMetaTags({
        path: route.fullPath,
        ogType: 'profile',
        ...author.value,
      }),
  script: isDataEmpty.value
    ? []
    : [
        {
          key: 'ld-json-person',
          type: 'application/ld+json',
          children: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Person',
            name: author.value.name,
            description: author.value.bio || `Автор медиа DOXA`,
            url: `https://ei3mgr4e-bsccljbcrq-ez.a.run.app/members/${author.value.slug.current}`,
            worksFor: {
              '@type': 'Organization',
              name: 'DOXA',
              url: 'https://ei3mgr4e-bsccljbcrq-ez.a.run.app',
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `https://ei3mgr4e-bsccljbcrq-ez.a.run.app/members/${author.value.slug.current}`,
            },
          }),
        },
      ],
});

async function fetchMoreArticles() {
  await refresh();
  moreArticles.value = [...moreArticles.value, ...moreArticlesChunk.value];

  if (moreArticlesChunk.value.length < articleChunkSize) {
    showMoreButton.value = false;
  }

  offset.value += articleChunkSize;
}
</script>

<template>
  <div v-if="!isDataEmpty" class="author-page">
    <LayoutDefaultPage is-header-shown>
      <AuthorHeader :author="author" />
      <ArticleOtherArticles
        v-if="authorArticles.length > 0"
        :articles="authorArticles"
        title="Публикации"
      />
      <div v-if="authorArticles.length > 0" class="author-page__more-articles">
        <div class="content-container">
          <AuthorMoreArticles
            v-if="moreArticles.length > 0"
            :articles="moreArticles"
          />
        </div>
        <div v-if="showMoreButton" class="author-page__more-button">
          <MoreContentButton :loading="pending" @click="fetchMoreArticles" />
        </div>
      </div>
    </LayoutDefaultPage>
  </div>
</template>

<style lang="postcss" scoped>
.author-page {
  position: relative;

  &__more-articles {
    margin-top: -100px;
    padding-bottom: 60px;
  }

  &__more-button {
    display: flex;
    justify-content: center;
    padding-top: 12px;
  }

  @media (--small-vp) {
    &__more-articles {
      margin-top: -52px;
    }
  }
}
</style>
